import React, { useMemo } from "react";
import { useIsFetching } from "@tanstack/react-query";
import { ListLayoutProps } from "@companion-core/shared/app/Interfaces/blocks";
import { ComponentCard } from "@companion-core/shared/app/Components/Cards/ComponentCard";
import { SkeletonCard } from "@companion-core/shared/app/Components/Cards/SkeletonCard/SkeletonCard";
import { useDynamicPageStore } from "@companion-core/shared/app/Store/dynamicPageStore";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { MosaicLayout } from "@companion-core/web/src/Components/MosaicLayout";
import "@companion-core/web/src/Assets/Styles/scss/extendedMosaicLayout.scss";

const ExtendedMosaicLayoutMultipleSkeleton = ({
  initialContent = false,
}: {
  initialContent?: boolean;
}) => {
  const skeletons = useMemo(
    () =>
      initialContent
        ? [
            <SkeletonCard key={1} imageOnly />,
            <SkeletonCard key={2} imageOnly />,
            <SkeletonCard key={3} imageOnly />,
          ]
        : [<SkeletonCard key={1} imageOnly />, <SkeletonCard key={2} imageOnly />],
    [initialContent],
  );
  return (
    <div
      data-testid="extended-mosaic-layout__skeleton--multiple"
      className={`extended-mosaic-layout__skeleton ${
        initialContent ? "extended-mosaic-layout__skeleton--initial-content" : ""
      }`}>
      <MosaicLayout data-testid="extended-mosaic-layout__content" items={skeletons} />
    </div>
  );
};

const ExtendedMosaicLayoutSingleSkeleton = () => {
  return (
    <div
      data-testid="extended-mosaic-layout__skeleton--single"
      className="extended-mosaic-layout__skeleton extended-mosaic-layout__skeleton--single">
      <SkeletonCard imageOnly />
    </div>
  );
};

export const ExtendedMosaicLayout = ({ title, queryComponents, blockItemId }: ListLayoutProps) => {
  const { pageSlug } = useDynamicPageStore();
  const isQueryComponentsFetching = useIsFetching({
    queryKey: ["block-item-components"],
  });
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const hasComponents = useMemo(() => !!queryComponents?.length, [queryComponents]);

  const ExtendedMosaicLayoutSkeleton = () => {
    if (!isMobileSize) {
      return <ExtendedMosaicLayoutMultipleSkeleton initialContent={!hasComponents} />;
    } else {
      return !hasComponents ? (
        <ExtendedMosaicLayoutMultipleSkeleton initialContent />
      ) : (
        <ExtendedMosaicLayoutSingleSkeleton />
      );
    }
  };

  return (
    <section
      data-testid={`extended-mosaic-layout--${blockItemId}`}
      className="extended-mosaic-layout">
      <h1
        data-testid={`extended-mosaic-layout__title--${blockItemId}`}
        className="extended-mosaic-layout__title super-title-1">
        {title}
      </h1>
      {queryComponents && (
        <MosaicLayout
          items={queryComponents?.map((component, index) => (
            <ComponentCard
              key={`extended-mosaic-layout-${component.id}`}
              type="include"
              component={component}
              pageSlug={pageSlug ?? ""}
              nbItems={queryComponents.length}
              position={index}
              url={window.location.href}
              blockItemId={blockItemId}
            />
          ))}
        />
      )}
      {!!isQueryComponentsFetching && <ExtendedMosaicLayoutSkeleton />}
    </section>
  );
};
